import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AnySchema, object as yupObject } from 'yup'
import { ComboboxItem } from '@node-space/ui-components'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { BusinessCustomerFormData, BusinessCustomerFormKey } from '../helpers/types'

type BusinessCustomerFormSchema = Record<BusinessCustomerFormKey, AnySchema>

export const useBusinessCustomerForm = (defaultValues: BusinessCustomerFormData) => {
  const { t } = useTranslation()
  const { getRequiredYupField } = useYupFields()

  const {
    data: countriesData = [],
    isFetching: isCountriesLoading,
    // TODO: handle error
    isError: isCountriesLoadingError,
  } = useCountriesQuery(true)

  const countryOptions: ComboboxItem[] = countriesData?.map(country => ({
    leftText: country?.name,
    value: country?.code,
  }))

  const formKeys = Object.keys(defaultValues) as BusinessCustomerFormKey[]

  const formSchema = yupObject(
    formKeys?.reduce((acc, key) => {
      acc[key] = getRequiredYupField({ label: t(`embeddedCustomers.businessForm.${key}`) })
      return acc
    }, {} as BusinessCustomerFormSchema)
  )
  const form = useForm<BusinessCustomerFormData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const { handleSubmit, setValue, control } = form
  const formValues = useWatch({ control })

  const { setInputField, setComboboxField } = useFormFields<BusinessCustomerFormKey>(
    formSchema,
    form,
    formValues
  )

  const getFormField = (key: BusinessCustomerFormKey) => {
    switch (key) {
      case 'countryOfIncorporation':
        return {
          ...setComboboxField({ key, items: countryOptions }),
          loading: isCountriesLoading,
          defaultValue: defaultValues?.[key],
        }
      default:
        return {
          ...setInputField({ key }),
          onChange: event => setValue(key, event?.target?.value, { shouldValidate: true }),
        }
    }
  }

  const formFields = formKeys?.reduce(
    (acc, key) => {
      acc[key] = getFormField(key)
      return acc
    },
    {} as Record<BusinessCustomerFormKey, any>
  )

  const displayData: Partial<BusinessCustomerFormData> = {
    countryOfIncorporation: countryOptions?.find(
      ({ value }) => value === formValues?.countryOfIncorporation
    )?.leftText as string,
  }

  return { formFields, displayData, handleSubmit }
}
